<template>
    <div :class="props.inline ? 'flex-row-with-gap' : 'flex-column-with-gap'" v-tooltip="props.disabled ? disabledTooltip : null">
        <label :class="{ 'center-title': props.centerTitle }">
            {{ props.title }}
            <slot name="definition" />
        </label>
        <primevue-select
            :disabled="props.disabled"
            :options="props.options"
            @change="updateSelection"
            class="dropdown"
            optionLabel="name"
            optionValue="id"
            :filter="props.filter"
            v-model="selectedItem"
            :virtualScrollerOptions="props.virtualScrollItemSize ? { itemSize: props.virtualScrollItemSize } : undefined"
        >
            <template #value="selection">
                <div v-if="selection" class="selection">
                    <div class="icon" v-if="selectedDecoratedItem?.icon" :style="{ backgroundColor: selectedDecoratedItem.color }">
                        <font-awesome-icon :icon="[selectedDecoratedItem.iconGroup || 'fas', selectedDecoratedItem.icon]" />
                    </div>
                    <div>
                        {{ selectedDecoratedItem?.name }}
                    </div>
                </div>
            </template>
            <template #option="{ option }">
                <div class="option-wrapper">
                    <div class="option" :class="{ disabled: option.isDisabled }">
                        <div class="icon" v-if="option.icon" :style="{ backgroundColor: option.color }">
                            <font-awesome-icon :icon="[option.iconGroup || 'fas', option.icon]" />
                        </div>
                        <div class="option-description">
                            {{ option.name }}
                        </div>
                    </div>
                    <font-awesome-icon v-if="option.isDisabled" :icon="['fas', 'lock-keyhole']" />
                </div>
            </template>
        </primevue-select>
    </div>
</template>

<script lang="ts" setup>
import { ref, watch, computed } from 'vue';
import PrimevueSelect from 'primevue/select';
import { IDecoratedDropdownItem } from '@/interfaces/general/IDecoratedDropdownItem.ts';
import { useUserAccessStore } from '@/stores/userAccessStore.ts';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps<{
    modelValue: string;
    options: Array<IDecoratedDropdownItem>;
    title?: string;
    disabled?: boolean;
    centerTitle?: boolean;
    inline?: boolean;
    filter?: boolean;
    virtualScrollItemSize?: number;
}>();

const userStore = useUserAccessStore();

function findById (id: string): IDecoratedDropdownItem | null {
    return props.options.find(item => item.id === id) ?? null;
}

const selectedItem = ref<string>(props.modelValue);

const selectedDecoratedItem = computed<IDecoratedDropdownItem | null>(() => findById(props.modelValue));

const disabledTooltip = (!userStore.storageTrackingConfig || userStore.storageTrackingConfig?.isTrial) ? 'Filter available with full Asset Tracking access' : 'Filter not available with current country selection';

const emit = defineEmits<{ 'update:modelValue': [string] }>();

function updateSelection (): void {
    emit('update:modelValue', selectedItem.value);
}

watch(() => props.modelValue, () => {
    if (selectedItem.value !== props.modelValue) selectedItem.value = props.modelValue;
});

</script>

<style lang="scss" scoped>
.center-title {
    align-self: center;
    font-size: var(--defaultFontSize);
    @include responsive('sm') {
        align-self: flex-start;
    }
}

.dropdown {
    width: 100%;
}

.option-wrapper {
    display: flex;
    justify-content: space-between;
    width: 100%;
    align-items: center;
    color: var(--primaryFont);

    .option {
        align-items: center;
        display: flex;
        gap: var(--defaultSpacing);

        &.disabled {
            pointer-events: none;
            filter: grayscale(1);
        }
    }
}

.icon {
    color: var(--primaryFont);
    border-radius: 50%;
    height: var(--largeFontSize);
    width: var(--largeFontSize);
    align-items: center;
    justify-content: center;
    display: flex;
    font-size: var(--smallFontSize);
}

.selection {
    align-items: center;
    display: flex;
    gap: var(--defaultSpacing);
}

label {
    white-space: nowrap;
}
.flex-column-with-gap {
    gap: var(--smallSpacing);
}
</style>
